/**
 * Đọc dữ liệu + quản trị qua BFF (mdmquery) tại /api. Session bằng cookie.
 * Backend tự giới hạn theo vai trò + cửa hàng của người đăng nhập.
 */
import { api, API_BASE } from './client';

// ── Thiết bị ──────────────────────────────────────────────────────────────────
export const listDevices          = ()      => api('/devices');
// checkinAllDevices: đẩy check-in NGAY tới mọi máy trong phạm vi (manager+). Trả {pushed}.
export const checkinAllDevices    = ()      => api('/devices/checkin-all', { method: 'POST' });
export const getDevice            = (udid)  => api(`/devices/${encodeURIComponent(udid)}`);
export const getDeviceInfo        = (udid)  => api(`/devices/${encodeURIComponent(udid)}/info`);
export const getInstalledApps     = (udid)  => api(`/devices/${encodeURIComponent(udid)}/apps`);
export const getInstalledProfiles = (udid)  => api(`/devices/${encodeURIComponent(udid)}/profiles`);
export const getStats             = ()      => api('/stats');
export const getRevenue           = ()      => api('/reports/revenue');
export const getOverdue           = ()      => api('/reports/overdue');
export const getAudit             = (q = '') => api('/audit' + (q ? `?q=${encodeURIComponent(q)}` : ''));
export const getDeviceAudit       = (udid)  => api(`/devices/${encodeURIComponent(udid)}/audit`);
export const getPushCert          = ()      => api('/pushcert');
export const uploadPushCert       = (pem, { force = false } = {}) => api('/pushcert' + (force ? '?force=1' : ''), { method: 'PUT', text: pem });
export const getAbmOrphans        = ()      => api('/abm-orphans');
export const getAbmDevices        = ()        => api('/abm-devices');
export const assignAbm            = (serials) => api('/abm-devices', { method: 'POST', json: { serials } });

export const getCommandResults = (udid, { limit = 50 } = {}) =>
  api(`/devices/${encodeURIComponent(udid)}/commands?limit=${limit}`);

// Ẩn iCloud cá nhân của khách (Bật/Tắt độc lập). GET trạng thái, POST {on} để đổi.
export const getICloudBlock = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/icloud-block`);
export const setICloudBlock = (udid, on) =>
  api(`/devices/${encodeURIComponent(udid)}/icloud-block`, { method: 'POST', json: { on: !!on } });

// Gán profile Hạn chế cho 1 máy — admin chủ động (chặn iCloud/Find My/định vị…;
// Org Lock lo khoá kích hoạt). Trả { command_uuid } để poll.
export const restrictDevice = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/restrict`, { method: 'POST' });

// Organization Activation Lock (server-side qua DEP, không cần Apple ID nào):
//   POST -> MDM tự sinh mã + gọi Apple để KHÓA; trả { bypass_code, dep_status, apple_response }.
//   GET  -> trạng thái { on, ok, at }; admin thêm { bypass_code } (backend redact cho manager/operator).
export const enableOrgLock = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/al-orglock`, { method: 'POST' });
export const getOrgLock = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/al-orglock`);

// NGUY HIỂM: Gỡ MDM (giữ dữ liệu) — gửi RemoveProfile, máy thoát MDM. KHÔNG tự gỡ
// Activation Lock (API Apple không ổn định) — mở khoá bằng nhập tay mã bypass. { ok, steps }.
export const clearOrglock = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/clear`, { method: 'POST' });

// Vị trí GPS gần nhất (kết quả DeviceLocation). null nếu chưa từng định vị.
export const getDeviceGeo = (udid) => api(`/devices/${encodeURIComponent(udid)}/geo`);

// Thông tin khách hàng + hợp đồng thanh toán gắn với thiết bị.
export const getDeviceOwner  = (udid)       => api(`/devices/${encodeURIComponent(udid)}/owner`);
export const saveDeviceOwner = (udid, data) => api(`/devices/${encodeURIComponent(udid)}/owner`, { method: 'PUT', json: data });

// Lịch sử thanh toán theo hợp đồng.
export const getPayments   = (udid)         => api(`/devices/${encodeURIComponent(udid)}/payments`);
export const addPayment    = (udid, data)   => api(`/devices/${encodeURIComponent(udid)}/payments`, { method: 'POST', json: data });
export const deletePayment = (udid, pid)    => api(`/devices/${encodeURIComponent(udid)}/payments/${pid}`, { method: 'DELETE' });

// ── Ảnh đính kèm (tab Khách hàng kind='owner' | Thanh toán kind='payment') ──────
// Body upload = Blob JPEG thô (client tự resize + nén). Server decode + re-encode
// lại JPEG (chống polyglot), lưu BYTEA. Xem cmd/mdmquery/images.go.
export const listImages = (udid, kind) =>
  api(`/devices/${encodeURIComponent(udid)}/images${kind ? `?kind=${kind}` : ''}`);

export const uploadImage = (udid, { kind, paymentId, blob, name } = {}) => {
  const q = new URLSearchParams({ kind });
  if (paymentId) q.set('payment_id', String(paymentId));
  if (name) q.set('name', name);
  return api(`/devices/${encodeURIComponent(udid)}/images?${q.toString()}`, {
    method: 'POST', blob, contentType: 'image/jpeg',
  });
};

export const deleteImage = (udid, imgId) =>
  api(`/devices/${encodeURIComponent(udid)}/images/${encodeURIComponent(imgId)}`, { method: 'DELETE' });

// URL trực tiếp để nhúng vào <img src=…> (cùng origin, kèm cookie).
export const imageURL = (udid, imgId) =>
  `${API_BASE}/devices/${encodeURIComponent(udid)}/images/${encodeURIComponent(imgId)}`;

// ── Thông báo lên màn hình máy (đặt bằng ẢNH NỀN — chỉ máy supervised) ─────────
// Apple MDM không có lệnh hiện banner; cách duy nhất không cần app là đặt ảnh nền
// cho cả màn khoá + màn chính. FE vẽ Tiêu đề + Nội dung thành ảnh (ô xem trước
// chính là ảnh gửi đi) rồi POST ảnh thô lên; BE kèm khoá không cho khách đổi lại.
export const getNotify = (udid) => api(`/devices/${encodeURIComponent(udid)}/notify`);

export const sendNotify = (udid, { title, blob }) =>
  api(`/devices/${encodeURIComponent(udid)}/notify?title=${encodeURIComponent(title || '')}`, {
    method: 'POST', blob, contentType: 'image/jpeg',
  });

// clearNotify: gỡ khoá ảnh nền + đặt lại nền trơn (không khôi phục được ảnh gốc của khách).
export const clearNotify = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/notify/clear`, { method: 'POST' });

// notifyAllDevices: gửi CÙNG một thông báo tới mọi máy trong phạm vi. Trả { sent }.
export const notifyAllDevices = ({ title, blob }) =>
  api(`/devices/notify-all?title=${encodeURIComponent(title || '')}`, {
    method: 'POST', blob, contentType: 'image/jpeg',
  });

// ── Lịch sử chủ trước (các đời enroll cũ đã lưu khi máy enroll lại) ─────────────
// Ảnh đời cũ vẫn serve qua imageURL(udid, imgId) như thường — dòng ảnh được GIỮ
// (gắn hist_id), chỉ ẩn khỏi danh sách sống.
export const getDeviceHistory = (udid) =>
  api(`/devices/${encodeURIComponent(udid)}/history`);

// Trạng thái "locked" do admin/manager đặt (NanoMDM không lưu).
export const setLocked   = (udid, message) => api(`/devices/${encodeURIComponent(udid)}/lock`,   { method: 'POST', json: message ? { message } : {} });
export const setUnlocked = (udid)          => api(`/devices/${encodeURIComponent(udid)}/unlock`, { method: 'POST', json: {} });
// CỔNG KHÁCH: enqueue DeviceLocation qua backend portal (khách không có API operator).
// App nội bộ dùng DeviceCommands.deviceLocation (operator /v1/enqueue) thay cho hàm này.
export const locateDevice = (udid) => api(`/devices/${encodeURIComponent(udid)}/locate`, { method: 'POST' });

// Gán thiết bị vào cửa hàng (location_id = 0/null để bỏ gán).
// Gán "người cài đặt" (staff) cho thiết bị — quyết định staff nào thấy máy. manager+.
export const setDeviceInstaller = (udid, userId) =>
  api(`/devices/${encodeURIComponent(udid)}/installer`, { method: 'PUT', json: { user_id: userId || 0 } });

export const setDeviceLocation = (udid, locationId) =>
  api(`/devices/${encodeURIComponent(udid)}/location`, { method: 'PUT', json: { location_id: locationId || 0 } });

// ── Kho (warehouse) ─────────────────────────────────────────────────────────
// getWarehouse: máy đang trong kho (manager+ xem toàn bộ; NV kho xem kho mình).
export const getWarehouse = () => api('/warehouse');
// pullDevice: kéo máy từ kho về cửa hàng (manager: bỏ trống -> cửa hàng mình).
export const pullDevice = (udid, locationId) =>
  api(`/devices/${encodeURIComponent(udid)}/pull`, { method: 'POST', json: locationId ? { location_id: locationId } : {} });
// returnToKho: đổi máy -> trả về kho (snapshot hồ sơ + xoá data + chuyển location kho).
export const returnToKho = (udid, warehouseId) =>
  api(`/devices/${encodeURIComponent(udid)}/return-to-kho`, { method: 'POST', json: { location_id: warehouseId } });

// ── Hoàn tất / Lịch sử máy đã từng quản lý ──────────────────────────────────
// getFinalizedDevices: danh sách máy đã "Hoàn tất" (đã gỡ MDM + đưa vào lịch sử).
export const getFinalizedDevices = () => api('/devices?finalized=1');
// finalizeDevice: đưa máy đã gỡ MDM vào lịch sử. unfinalizeDevice: khôi phục về danh sách chính.
export const finalizeDevice   = (udid) => api(`/devices/${encodeURIComponent(udid)}/finalize`, { method: 'POST' });
export const unfinalizeDevice = (udid) => api(`/devices/${encodeURIComponent(udid)}/unfinalize`, { method: 'POST' });
// terminateContract: NGƯNG hợp đồng -> tính tiền lời + đưa vào lịch sử máy.
// data: { scenario:'keep'|'return', settle, refund, note }
export const terminateContract = (udid, data) => api(`/devices/${encodeURIComponent(udid)}/terminate`, { method: 'POST', json: data });

// ── Cửa hàng (locations) ────────────────────────────────────────────────────
export const listLocations   = ()             => api('/locations');
export const createLocation  = (data)         => api('/locations', { method: 'POST', json: data });
export const updateLocation  = (id, data)     => api(`/locations/${id}`, { method: 'PATCH', json: data });
export const deleteLocation  = (id)           => api(`/locations/${id}`, { method: 'DELETE' });

// ── Khách hàng thuê bao (customers) — chỉ admin ─────────────────────────────
export const getCustomerReport = ()           => api('/reports/customers');
export const listCustomers   = ()             => api('/customers');
export const createCustomer  = (data)         => api('/customers', { method: 'POST', json: data });
export const updateCustomer  = (id, data)     => api(`/customers/${id}`, { method: 'PATCH', json: data });
export const deleteCustomer  = (id)           => api(`/customers/${id}`, { method: 'DELETE' });
// Duyệt khách do CTV tạo (pending -> active, đặt hạn thuê bao).
export const approveCustomer = (id)           => api(`/customers/${id}/approve`, { method: 'POST' });

// Thanh toán của khách (thu tiền thuê bao)
// ── Tài khoản CỔNG KHÁCH của một khách hàng (customer_users) ─────────────────
// Tách hẳn khỏi app_users: khách không dùng chung tài khoản với nhân viên Zentra.
// Vai trò owner (cả chuỗi) / manager | staff (kèm location_id). Xem portal_admin.go.
export const listCustomerPortalUsers  = (cid)            => api(`/customers/${cid}/portal-users`);
export const createCustomerPortalUser = (cid, data)      => api(`/customers/${cid}/portal-users`, { method: 'POST', json: data });
export const updateCustomerPortalUser = (cid, uid, data) => api(`/customers/${cid}/portal-users/${uid}`, { method: 'PATCH', json: data });
export const deleteCustomerPortalUser = (cid, uid)       => api(`/customers/${cid}/portal-users/${uid}`, { method: 'DELETE' });

export const listCustomerPayments  = (cid)        => api(`/customers/${cid}/payments`);
export const addCustomerPayment    = (cid, data)  => api(`/customers/${cid}/payments`, { method: 'POST', json: data });
export const deleteCustomerPayment = (cid, pid)   => api(`/customers/${cid}/payments/${pid}`, { method: 'DELETE' });

// Cộng tác viên + chi hoa hồng
export const listCollaborators   = ()            => api('/collaborators');
export const createCollaborator  = (data)        => api('/collaborators', { method: 'POST', json: data });
export const updateCollaborator  = (id, data)    => api(`/collaborators/${id}`, { method: 'PATCH', json: data });
export const deleteCollaborator  = (id)          => api(`/collaborators/${id}`, { method: 'DELETE' });
export const listCollaboratorCustomers = (id)    => api(`/collaborators/${id}/customers`);
// Tài khoản đăng nhập CỔNG CTV (collaborator_users) — admin quản lý.
export const listCollaboratorUsers  = (id)          => api(`/collaborators/${id}/users`);
export const createCollaboratorUser = (id, data)    => api(`/collaborators/${id}/users`, { method: 'POST', json: data });
export const updateCollaboratorUser = (id, uid, data) => api(`/collaborators/${id}/users/${uid}`, { method: 'PATCH', json: data });
export const deleteCollaboratorUser = (id, uid)     => api(`/collaborators/${id}/users/${uid}`, { method: 'DELETE' });
export const listPayouts   = (id)                => api(`/collaborators/${id}/payouts`);
export const addPayout     = (id, data)          => api(`/collaborators/${id}/payouts`, { method: 'POST', json: data });
export const deletePayout  = (pid)               => api(`/payouts/${pid}`, { method: 'DELETE' });
export const getFinanceReport = ()               => api('/reports/finance');
export const getLedger    = ({ from = '', to = '', q = '' } = {}) =>
  api(`/reports/ledger?from=${from}&to=${to}&q=${encodeURIComponent(q)}`);

// ── Bằng chứng thu/chi (ảnh + PDF) ────────────────────────────────────────────
// kind: 'thu' (thu khách) | 'chi_hh' (chi hoa hồng) | 'chi_khac' (chi phí ngoại lệ),
// id = id dòng giao dịch. Body upload là file NGUYÊN BẢN (không nén lại như ảnh máy)
// vì bản scan chứng từ phải giữ đúng bản gốc để đối chiếu. Xem cmd/mdmquery/ledgerfiles.go.
// Chi tiết một giao dịch trong sổ: phần chung (mã, ngày, số tiền, ai ghi) + phần
// riêng theo loại (kỳ/hình thức, CTV, hạng mục…) + bằng chứng kèm sẵn.
export const getLedgerTxn = (kind, id) => api(`/ledger/${kind}/${id}`);

export const listLedgerFiles = (kind, id) => api(`/ledger/${kind}/${id}/files`);

export const uploadLedgerFile = (kind, id, file) =>
  api(`/ledger/${kind}/${id}/files?name=${encodeURIComponent(file.name || '')}`, {
    method: 'POST', blob: file, contentType: file.type || 'application/octet-stream',
  });

export const deleteLedgerFile = (kind, id, fileId) =>
  api(`/ledger/${kind}/${id}/files/${encodeURIComponent(fileId)}`, { method: 'DELETE' });

// URL trực tiếp để mở trong tab mới / nhúng <img> (cùng origin, kèm cookie).
export const ledgerFileURL = (kind, id, fileId) =>
  `${API_BASE}/ledger/${kind}/${id}/files/${encodeURIComponent(fileId)}`;
export const listExpenses = ({ from = '', to = '' } = {}) => api(`/expenses?from=${from}&to=${to}`);
export const addExpense   = (data)               => api('/expenses', { method: 'POST', json: data });
export const deleteExpense = (id)                => api(`/expenses/${id}`, { method: 'DELETE' });

// ── Người dùng (users) ──────────────────────────────────────────────────────
export const listUsers   = ()         => api('/users');
export const createUser  = (data)     => api('/users', { method: 'POST', json: data });
export const updateUser  = (id, data) => api(`/users/${id}`, { method: 'PATCH', json: data });
export const deleteUser  = (id)       => api(`/users/${id}`, { method: 'DELETE' });

// ── CỔNG KHÁCH — hồ sơ doanh nghiệp (gói, thời hạn, hạn mức, pháp nhân) ──────
export const getCompany = () => api('/company');

// ── Tài khoản quét ABM (Keycloak realm abm) — CHỈ admin ───────────────────
export const listKCUsers  = (q)     => api('/keycloak/users' + (q ? `?q=${encodeURIComponent(q)}` : ''));
export const patchKCUser  = (id, d) => api(`/keycloak/users/${encodeURIComponent(id)}`, { method: 'PATCH', json: d });
export const createKCUser = (d)     => api('/keycloak/users', { method: 'POST', json: d });
export const deleteKCUser = (id)    => api(`/keycloak/users/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ── CỔNG KHÁCH — quản trị chuỗi (gói Chuỗi 300 / Vô Hạn / Doanh Nghiệp) ──────
// Backend: cmd/mdmquery/portal_staff.go. Mọi endpoint đều kẹp customer_id của phiên
// nên FE KHÔNG gửi và KHÔNG gửi được định danh khách.
export const listChainStaff   = ()        => api('/staff');
export const createChainStaff = (data)    => api('/staff', { method: 'POST', json: data });
export const updateChainStaff = (id, d)   => api(`/staff/${id}`, { method: 'PATCH', json: d });
export const deleteChainStaff = (id)      => api(`/staff/${id}`, { method: 'DELETE' });
// Cửa hàng của chuỗi (chỉ đọc — tạo/sửa cửa hàng vẫn do ZENTRA làm).
export const listChainStores  = ()        => api('/stores');
// Chuyển máy sang cửa hàng khác trong chuỗi.
export const setDeviceStore   = (udid, locationID) =>
  api(`/devices/${encodeURIComponent(udid)}/store`, { method: 'POST', json: { location_id: locationID } });
// Báo cáo tổng hợp theo từng cửa hàng: { stores: [...], total: {...} }.
export const getChainReport   = ()        => api('/reports/chain');
